// I contenuti della guida (ridisegno 28/10/2026). Stanno qui, separati dalla
// pagina, perche' la ricerca li legge tutti e un test controlla che non
// tornino nomi morti (Thunderbird, Electron, Poleepo per le LDV).
//
// Regola di scrittura: frasi brevi, parole di tutti i giorni, il nome del
// tasto ESATTO come appare in OM, tra virgolette basse.

/** Chi fa il passo: OM da solo, la persona, o tutti e due a seconda. */
export type Chi = "solo" | "mano" | "misto";

export type Tappa = {
  nome: string;
  chi: Chi;
  testo: string;
};

/** Il giro di un ordine, dall'ingresso alla fattura. */
export const GIRO_ORDINE: Tappa[] = [
  {
    nome: "Entra",
    chi: "solo",
    testo:
      "Ogni pochi minuti OM legge gli ordini da Kery, Amazon, Leroy Merlin, Maisons du Monde, Vente-Unique, Metro, ManoMano, Brico Bravo e Temu.",
  },
  {
    nome: "Fornitore",
    chi: "misto",
    testo:
      "Se il pezzo è nel magazzino BS COM parte da lì. Se no OM lo ordina al fornitore: da solo per AllService, Cosma, Bizzotto, Itamoby, Ityhome e Web Furniture, a mano per gli altri.",
  },
  {
    nome: "LDV",
    chi: "misto",
    testo:
      "L'etichetta del corriere (SDA, BRT o Poste Delivery). «Genera LDV» la crea, «Invia LDV» la manda al fornitore che prepara il pacco.",
  },
  {
    nome: "Ritiro",
    chi: "misto",
    testo:
      "Il corriere passa a prendere il pacco. OM prenota il ritiro SDA da solo quando il fornitore dice che la merce è pronta.",
  },
  {
    nome: "Spedito",
    chi: "solo",
    testo:
      "OM dice al canale che l'ordine è partito, col tracking. Lo fa alla prima scansione del corriere o alla scadenza, alle 18 e alle 23.",
  },
  {
    nome: "Fattura",
    chi: "solo",
    testo:
      "Alle 18 OM emette in Eureka i documenti pronti, uno alla volta e in ordine di data. Le fatture da mandare allo SdI le invii tu da Eureka.",
  },
];

export type Regola = { titolo: string; perche: string };

/** Le regole che, se non le sai, fanno danni veri. */
export const REGOLE_ORO: Regola[] = [
  {
    titolo: "Una LDV non vuol dire spedito.",
    perche:
      "Generare o mandare l'etichetta non avvisa il canale. Lo spedito lo dichiarano solo «Spedito», «Conferma a <canale>» o l'automatismo della scadenza.",
  },
  {
    titolo: "Il magazzino BS COM vince su tutto.",
    perche:
      "Se il pezzo è a stock in MATECPRO, OM non lo ricompra dal fornitore. L'unica uscita è «Manda al fornitore» sulla pagina dell'ordine.",
  },
  {
    titolo: "AllService vince sugli altri fornitori.",
    perche:
      "La merce è già da loro in conto terzi: ordinarla altrove vuol dire comprarla due volte.",
  },
  {
    titolo: "In Eureka non si cancella niente.",
    perche:
      "Ogni errore è un documento fiscale sbagliato. Per questo OM emette uno alla volta, in ordine di data, e si ferma al primo dubbio.",
  },
  {
    titolo: "Le mail sono informazioni, mai comandi.",
    perche: "Nessuna azione parte perché lo chiede una mail, nemmeno se sembra del fornitore.",
  },
  {
    titolo: "Poleepo serve solo a far entrare gli ordini Amazon.",
    perche:
      "LDV, tracking, spedito e fatture si fanno tutti in OM, diretti col corriere e col canale.",
  },
  {
    titolo: "Oltre 15 colli SDA si passa a Poste Delivery.",
    perche: "OM lo chiede sempre prima. SDA non accetta spedizioni più grandi.",
  },
  {
    titolo: "Ogni automatismo si spegne da Impostazioni.",
    perche:
      "Se qualcosa fa una cosa sbagliata, lo spegni con l'interruttore e smette subito. Riaccenderlo chiede conferma se agisce fuori da OM.",
  },
];

export type Voce = {
  /** Cosa vuoi fare, detto come lo diresti tu. */
  titolo: string;
  passi: string[];
};

export type Pagina = {
  id: string;
  nome: string;
  /** Rotte da aprire, col nome del menu. */
  apri: { nome: string; href: string }[];
  /** Una riga: a cosa serve. */
  serve: string;
  /** Cosa fa OM qui da solo, senza che tu tocchi niente. */
  daSolo?: string[];
  voci: Voce[];
};

export const PAGINE: Pagina[] = [
  {
    id: "ordini",
    nome: "Ordini",
    apri: [{ nome: "Ordini", href: "/ordini" }],
    serve: "Tutti gli ordini di tutti i canali, in un elenco solo.",
    daSolo: [
      "Legge gli ordini nuovi e aggiorna quelli che cambiano.",
      "Ordina ai fornitori automatici e manda la LDV a Itamoby e Ityhome.",
      "Dichiara lo spedito ai canali e legge il tracking dei corrieri.",
    ],
    voci: [
      {
        titolo: "Trovare gli ordini da lavorare",
        passi: [
          "Usa i filtri in alto: «Da evadere», «Spedizione Pronta», «Non ordinato», «Ordinato».",
          "«Da pagare» e «Pagato» sono gli ordini ai fornitori. «Da incassare» sono i soldi che aspettiamo noi.",
          "«Attesa pagamento» sono ordini non ancora pagati dal cliente: entrano in OM ma non si lavorano.",
        ],
      },
      {
        titolo: "Ordinare al fornitore",
        passi: [
          "Apri l'ordine. Il tasto cambia col fornitore: «Ordina al fornitore», «Ordina a Cosma», «Invia ordine AS».",
          "Se il pezzo è nel magazzino BS COM il tasto non c'è: parte da noi.",
          "«Riordina» rifà l'ordine quando il primo è andato male.",
        ],
      },
      {
        titolo: "Fare e mandare la LDV",
        passi: [
          "«Genera LDV» crea l'etichetta. Puoi farlo anche su più ordini insieme dall'elenco.",
          "«Invia LDV» la manda al fornitore con la richiesta di ritiro.",
          "«Stampa Zebra» stampa l'etichetta sulla stampante del magazzino.",
        ],
      },
      {
        titolo: "Dire al canale che è partito",
        passi: [
          "Di solito lo fa OM da solo. Se serve a mano: «Spedito» oppure «Conferma a <canale>».",
          "Prima controlla che il tracking sia quello giusto: una volta mandato, il canale lo mostra al cliente.",
        ],
      },
      {
        titolo: "Reso, sostituzione, rimborso",
        passi: [
          "Dalla pagina dell'ordine: «Reso», «Sostituzione» o «Rimborso».",
          "Il reso crea l'etichetta per il ritorno. La trovi poi in Ritiri e resi › Resi.",
        ],
      },
      {
        titolo: "Telefonate col cliente",
        passi: [
          "«Registra chiamata» segna la telefonata sull'ordine e sulla scheda del cliente.",
          "La storia dell'ordine (mail, chiamate, spedizioni) è nella colonna a destra.",
        ],
      },
    ],
  },
  {
    id: "fornitori",
    nome: "Fornitori",
    apri: [{ nome: "Anagrafiche › Fornitori", href: "/fornitori" }],
    serve: "Una scheda per fornitore: contatti, catalogo, giacenze e strumenti.",
    daSolo: [
      "Legge le mail dei fornitori: conferme, merce pronta, tracking.",
      "Aggiorna le giacenze del magazzino BS COM da MATECPRO.",
    ],
    voci: [
      {
        titolo: "Vedere cosa ha un fornitore",
        passi: [
          "Scegli la tab del fornitore in alto.",
          "Dentro trovi il catalogo, i prezzi e le giacenze che ci manda.",
        ],
      },
      {
        titolo: "Caricare un catalogo o un listino",
        passi: [
          "Nella scheda del fornitore usa l'importazione del catalogo.",
          "OM controlla gli SKU uno per uno: il codice deve essere identico, mai simile.",
        ],
      },
      {
        titolo: "Accendere il ritiro automatico",
        passi: [
          "Nella scheda del fornitore o nella pagina Ritiri corriere c'è l'interruttore del ritiro automatico.",
          "Si accende solo per chi ci manda un segnale «merce pronta».",
        ],
      },
    ],
  },
  {
    id: "ritiri",
    nome: "Ritiri e resi",
    apri: [
      { nome: "Ritiri corriere", href: "/ritiri" },
      { nome: "Resi", href: "/resi" },
      { nome: "Reclami corriere", href: "/reclami" },
    ],
    serve: "I ritiri del corriere dai magazzini, le spedizioni di reso e i reclami a Bracchi.",
    daSolo: [
      "Prenota il ritiro SDA quando la merce è pronta, entro 3 giorni lavorativi.",
      "Ritiro in giornata solo se prenotato entro le 11:50.",
      "Ogni ora legge le mail con Bracchi e aggiorna i reclami: aperti, rimborsati, respinti.",
    ],
    voci: [
      {
        titolo: "Seguire un reclamo a Bracchi",
        passi: [
          "Apri Reclami corriere: in alto le pratiche dove «Tocca a noi», con la domanda di Bracchi.",
          "Rispondi a Bracchi dalla posta come sempre. Al giro dopo la pratica torna «In verifica da SDA».",
          "Il modulo «Apertura pratica SDA» si scarica dalla pagina dell'ordine, col tasto «Contestazione».",
        ],
      },
      {
        titolo: "Prenotare un ritiro a mano",
        passi: [
          "Apri il magazzino e premi «Prenota ritiro».",
          "«Prenota solo ritiro» prenota il passaggio del corriere senza toccare gli ordini.",
          "Un ritiro al giorno per magazzino. Se ne serve un secondo, usa «Aggiungi ritiro».",
        ],
      },
      {
        titolo: "Se SDA non risponde",
        passi: [
          "Il ritiro resta «esito ignoto» e OM non lo ripete da solo: SDA non riconosce i doppioni.",
          "Controlla sul portale SDA prima di riprovare.",
        ],
      },
      {
        titolo: "Seguire un reso",
        passi: [
          "In Resi vedi ogni reso e sostituzione con la sua etichetta.",
          "In Italia SDA, all'estero BRT.",
        ],
      },
    ],
  },
  {
    id: "messaggi",
    nome: "Messaggi",
    apri: [
      { nome: "Messaggi clienti", href: "/messaggi" },
      { nome: "Mail fornitori", href: "/mail" },
    ],
    serve: "Le domande dei clienti dai canali e la posta coi fornitori.",
    voci: [
      {
        titolo: "Rispondere a un cliente",
        passi: [
          "Apri il messaggio: a destra vedi l'ordine, il tracking e la storia.",
          "Scrivi in italiano. «Traduci la bozza nella lingua del cliente» la traduce prima dell'invio.",
          "«Traduci tutto» traduce in italiano i messaggi del cliente.",
        ],
      },
      {
        titolo: "Scrivere a un fornitore",
        passi: [
          "Mail fornitori raccoglie la posta per ordine.",
          "Le risposte dei fornitori le legge OM: conferme e tracking finiscono da soli sull'ordine.",
        ],
      },
    ],
  },
  {
    id: "fatture",
    nome: "Fatture",
    apri: [
      { nome: "Fatturazione", href: "/fatturazione" },
      { nome: "Corrispettivi Amazon", href: "/fatturazione/corrispettivi" },
      { nome: "Fatture estere", href: "/fatturazione/fatture-estere" },
      { nome: "Saldi", href: "/fatturazione/saldi" },
    ],
    serve:
      "Fatture e storni su Eureka, i corrispettivi Amazon, le fatture dei fornitori esteri e il saldo sul conto di ogni marketplace.",
    daSolo: [
      "Alle 16 avvisa su Slack cosa manca per il giro.",
      "Alle 18 emette in Eureka fino a 30 documenti pronti.",
      "Cerca da solo P.IVA, codice fiscale, SDI e PEC dei clienti che chiedono fattura.",
      "Crea gli storni quando un canale rimborsa.",
    ],
    voci: [
      {
        titolo: "Capire cosa c'è da fare",
        passi: [
          "«Da fatturare»: ordini pronti per il documento.",
          "«Storni»: rimborsi che chiedono una nota di credito.",
          "«Da inviare allo SdI»: fatture già in Eureka da mandare con F11.",
          "«In attesa»: manca un dato. Il motivo è scritto sulla riga.",
        ],
      },
      {
        titolo: "Sbloccare una riga in attesa",
        passi: [
          "Leggi il motivo sulla riga e sistema il dato che manca.",
          "Cambiare lo stato non sblocca niente: blocca il motivo, non lo stato.",
        ],
      },
      {
        titolo: "Corrispettivi Amazon",
        passi: ["Il file mensile per l'amministrazione, fatto dal tax report di Seller Central."],
      },
      {
        titolo: "Fatture estere",
        passi: [
          "Le fatture dei fornitori esteri, per fare le autofatture.",
          "«Da fare», «Fatte», «Tutte». Quelle con la AUT già in Eureka si spuntano da sole.",
          "«Crea in Eureka» crea l'autofattura.",
        ],
      },
      {
        titolo: "Saldi",
        passi: [
          "Quanto c'è adesso sul conto di ogni marketplace, di Shopify Payments e di PayPal. Si aggiorna da solo ogni ora; «Aggiorna ora» lo legge subito.",
          "«Senza API»: il canale non dà il saldo a OM. Il motivo dice dove guardarlo.",
          "«Non letto»: l'API non ha risposto. Resta l'ultimo saldo buono, con la sua ora.",
          "«Calcolato da noi», nel riquadro tratteggiato: dove il canale non dà il saldo (Brico Bravo, ManoMano) OM lo rifà con la regola di pagamento del canale. Non entra nel totale vero.",
        ],
      },
    ],
  },
  {
    id: "clienti",
    nome: "Clienti e BI",
    apri: [
      { nome: "Anagrafiche › Clienti", href: "/clienti" },
      { nome: "BI", href: "/dashboard" },
    ],
    serve: "La scheda unica di ogni cliente e i numeri dell'azienda.",
    voci: [
      {
        titolo: "Trovare un cliente",
        passi: [
          "Cerca per nome, mail o telefono. La scheda mette insieme ordini, resi e chiamate di tutti i canali.",
        ],
      },
      {
        titolo: "Leggere i numeri",
        passi: [
          "BI mostra ordini, fatturato e margine per canale.",
          "Il margine è netto: tolti costo fornitore, commissioni e spedizione.",
        ],
      },
    ],
  },
  {
    id: "impostazioni",
    nome: "Impostazioni",
    apri: [{ nome: "Impostazioni", href: "/impostazioni" }],
    serve: "Come gira OM: automatismi, connessioni, accessi e diagnostica.",
    voci: [
      {
        titolo: "Vedere se va tutto bene",
        passi: [
          "In cima c'è scritto «Tutto funziona» oppure quante cose sono da guardare.",
          "Il pallino rosso nell'indice ti porta dritto al problema.",
        ],
      },
      {
        titolo: "Spegnere o accendere un automatismo",
        passi: [
          "In Automatismi trovi ogni lavoro che OM fa da solo, con l'ultimo giro.",
          "L'interruttore lo spegne subito. «Esegui ora» lo fa partire senza aspettare.",
          "Quelli con «Agisce fuori» comprano, scrivono a clienti o fornitori o emettono fatture: chiedono conferma.",
        ],
      },
      {
        titolo: "Cambiare password o tema",
        passi: ["Sezione Accesso: password e secondo fattore.", "Sezione Aspetto: chiaro o scuro."],
      },
    ],
  },
  {
    id: "telefono",
    nome: "Telefono e Cerca",
    apri: [],
    serve: "OM funziona anche dal telefono, e trovi tutto da un campo solo.",
    voci: [
      {
        titolo: "Cercare qualsiasi cosa",
        passi: [
          "Premi Ctrl+K (sul Mac ⌘K) da qualunque pagina.",
          "Scrivi un numero d'ordine, un cliente, un telefono, un tracking o uno SKU.",
        ],
      },
      {
        titolo: "Usare OM dal telefono",
        passi: [
          "Apri om.bscom.it dal browser del telefono ed entra come al computer.",
          "Il menu è in basso. La lente apre la stessa ricerca.",
        ],
      },
    ],
  },
];

export type Problema = { sintomo: string; cosaFare: string[] };

export const SE_NON_VA: Problema[] = [
  {
    sintomo: "La pagina non si carica o è bianca",
    cosaFare: [
      "Ricarica con Ctrl+F5. Durante un aggiornamento OM resta fermo qualche secondo.",
      "Se resta bianca, guarda Impostazioni › Diagnostica.",
    ],
  },
  {
    sintomo: "Un ordine nuovo non compare",
    cosaFare: [
      "Aspetta il prossimo giro (pochi minuti) o premi «Sincronizza» in Ordini.",
      "Controlla in Impostazioni che l'automatismo del canale non sia in errore.",
    ],
  },
  {
    sintomo: "Un automatismo è in errore",
    cosaFare: [
      "In Impostazioni apri la riga: il riquadro rosso dice il motivo.",
      "Se fa danni, spegnilo con l'interruttore. Poi avvisa chi segue OM.",
    ],
  },
  {
    sintomo: "Il canale non vede lo spedito",
    cosaFare: [
      "Controlla che sull'ordine ci sia il tracking.",
      "Prova «Conferma a <canale>» dalla pagina dell'ordine.",
    ],
  },
  {
    sintomo: "Una fattura resta in attesa",
    cosaFare: [
      "Leggi il motivo sulla riga in Fatturazione: dice quale dato manca.",
      "Non fare il documento a mano in Eureka senza averlo detto: rischi il doppione.",
    ],
  },
];

export type Termine = { parola: string; significa: string };

export const GLOSSARIO: Termine[] = [
  {
    parola: "LDV",
    significa: "Lettera di vettura: l'etichetta del corriere da attaccare al pacco.",
  },
  {
    parola: "Borderò",
    significa: "Il foglio che il corriere firma al ritiro, con l'elenco dei pacchi.",
  },
  { parola: "Tracking", significa: "Il codice per seguire il pacco sul sito del corriere." },
  { parola: "Collo", significa: "Un singolo pacco. Un ordine può avere più colli." },
  {
    parola: "Nativo",
    significa: "Un ordine che entra in OM direttamente dal canale, senza passare da Poleepo.",
  },
  {
    parola: "Lane",
    significa: "Un automatismo: un lavoro che OM ripete da solo a orari o intervalli.",
  },
  {
    parola: "SF",
    significa: "Corrispettivo: il documento per il privato italiano che non chiede fattura.",
  },
  { parola: "FA", significa: "Fattura vera, per chi ha P.IVA o la chiede." },
  {
    parola: "OSS",
    significa: "Fattura a un privato in un altro paese UE, con l'IVA di quel paese.",
  },
  { parola: "ST / RC / RCO", significa: "Gli storni: ST annulla una SF, RC una FA, RCO una OSS." },
  { parola: "AUT", significa: "Autofattura, per le fatture dei fornitori esteri." },
  {
    parola: "SdI",
    significa: "Sistema di Interscambio: il canale dell'Agenzia delle Entrate per le fatture.",
  },
  { parola: "Eureka", significa: "Il gestionale dove nascono le fatture di BS COM." },
  { parola: "PIM", significa: "L'app con le schede prodotto e i costi dei fornitori." },
  { parola: "MATECPRO", significa: "Il magazzino: da qui OM sa cosa abbiamo a stock." },
];

/** Tutto il testo di una pagina in una stringa, per la ricerca. */
export function testoPagina(p: Pagina): string {
  return [p.nome, p.serve, ...(p.daSolo ?? []), ...p.voci.flatMap((v) => [v.titolo, ...v.passi])]
    .join(" ")
    .toLowerCase();
}

/** Normalizza per la ricerca: minuscolo, senza accenti. */
export function normalizza(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
